<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
        <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Created Flyers</p>
            <h1 class="mt-2 text-3xl font-bold text-slate-950">Flyer Timeline</h1>
            <p class="mt-2 text-sm font-medium text-slate-500">
              Manage flyers you have created and continue editing drafts.
            </p>
          </div>

          <div class="rounded-2xl bg-slate-100 px-4 py-3 text-center">
            <div class="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Posts</div>
            <div class="mt-1 text-2xl font-bold text-slate-950">{{ flyers.length }}</div>
          </div>
        </div>
      </div>

      <div class="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div class="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
          <div>
            <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Archive</p>
            <h2 class="mt-2 text-2xl font-bold text-slate-950">Posts grouped by date</h2>
          </div>
          <span class="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            {{ flyers.length }} total
          </span>
        </div>

        <div v-if="isLoading" class="mt-5 grid gap-8">
          <section class="grid gap-4">
            <div class="flex items-center gap-3">
              <span class="h-9 w-9 animate-pulse rounded-full bg-slate-100"></span>
              <div class="grid gap-2">
                <span class="h-4 w-32 animate-pulse rounded-full bg-slate-200"></span>
                <span class="h-3 w-24 animate-pulse rounded-full bg-slate-100"></span>
              </div>
            </div>

            <div class="created-timeline-grid mt-1">
              <div
                v-for="(column, columnIndex) in skeletonFlyerColumns"
                :key="`created-skeleton-column-${columnIndex}`"
                class="created-timeline-column"
              >
                <FlyerCardSkeleton v-for="index in column" :key="`created-skeleton-${index}`" :index="index" />
              </div>
            </div>
          </section>
        </div>

        <div v-else-if="!groupedFlyers.length" class="py-12 text-center">
          <p class="m-0 text-lg font-bold text-slate-900">No flyers yet</p>
          <p class="mt-2 text-sm font-medium text-slate-500">
            Start with a new post and it will appear here.
          </p>
        </div>

        <div v-else class="mt-5 grid gap-8">
          <section v-for="group in groupedFlyers" :key="group.key" class="grid gap-4">
            <div class="sticky top-0 z-10 -mx-1 flex items-center gap-3 bg-white/95 px-1 py-1 backdrop-blur">
              <span class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="3" />
                  <path d="M8 2v4M16 2v4M3 10h18" />
                </svg>
              </span>
              <div>
                <h3 class="m-0 text-lg font-bold text-slate-950">{{ group.label }}</h3>
                <p class="m-0 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  {{ group.items.length }} flyer{{ group.items.length === 1 ? '' : 's' }}
                </p>
              </div>
            </div>

            <div class="created-timeline-grid mt-1">
              <div
                v-for="(column, columnIndex) in group.columns"
                :key="`${group.key}-column-${columnIndex}`"
                class="created-timeline-column"
              >
                <FlyerCard
                  v-for="flyer in column"
                  :key="flyer.id"
                  :flyer="flyer"
                  view="masonry"
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import FlyerCard from '@/components/common/FlyerCard.vue'
import FlyerCardSkeleton from '@/components/common/FlyerCardSkeleton.vue'
import api from '@/services/api'
import { sampleFlyers } from '@/data/flyers'
import { formatFlyerListDate, parseApiDate } from '@/utils/date'
import { useSessionStore } from '@/stores/session'

const session = useSessionStore()
const isLoading = ref(false)
const flyers = ref([])
const masonryColumnCount = ref(1)

const distributeByColumn = (items, columnCount) => {
  const count = Math.max(1, Number(columnCount) || 1)
  const columns = Array.from({ length: count }, () => [])
  items.forEach((item, index) => {
    columns[index % count].push(item)
  })
  return columns
}

const skeletonFlyerColumns = computed(() => distributeByColumn(Array.from({ length: 8 }, (_, index) => index + 1), masonryColumnCount.value))

const updateMasonryColumnCount = () => {
  if (typeof window === 'undefined') return
  masonryColumnCount.value = window.innerWidth >= 1280 ? 4 : window.innerWidth >= 768 ? 2 : 1
}

const normalizeStatus = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === '1' || normalized === 'active' || normalized === 'published' || normalized === 'true') {
    return 'Active'
  }
  if (normalized === '0' || normalized === 'inactive' || normalized === 'draft' || normalized === 'false') {
    return 'Inactive'
  }
  return 'Inactive'
}

const normalizeFlyer = (item = {}, index = 0) => {
  const dateValue = item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || item.date || ''
  const parsedDate = parseApiDate(dateValue)
  const fallbackImage = Array.isArray(item.images) ? item.images[0] : ''
  const locationText = item.city || item.location || item.area || item.locality || item.state || item.address || item.street || item.country_id || item.country || ''
  const rawCategory = item.category || item.content_type || item.category_id || ''
  const categoryName =
    item.category_name ||
    item.catergory_name ||
    item.cat_name ||
    item.content_type_name ||
    item.flyer_category ||
    item.category_title ||
    (rawCategory && Number.isNaN(Number(rawCategory)) ? rawCategory : '') ||
    'Flyer'

  return {
    id: item.id || item.flyer_id || `created-${index}`,
    title: item.title || item.name || item.flyer_title || '',
    category: categoryName,
    category_name: categoryName,
    contentTypeId: item.content_type || item.content_type_id || item.category_id || '',
    description: item.description || item.desc || item.caption || '',
    image: item.image || item.flyer_image || item.image_url || fallbackImage || '',
    param_code: item.param_code || item.paramCode || item.code || item.color_code || item.colorCode || item.color || '',
    code: item.code || item.param_code || item.paramCode || item.color_code || item.colorCode || item.color || '',
    accent: item.accent || item.categoryColor || '#64748b',
    address: item.address || item.location || item.city || '',
    city: locationText,
    country_id: item.country_id || item.country || '',
    country: item.country || item.country_id || '',
    state: item.state || '',
    area: item.area || '',
    locality: item.locality || '',
    street: item.street || '',
    location: locationText,
    status: normalizeStatus(item.status ?? item.publish_status),
    userId: item.user_id || item.userId || item.userid || session.userId,
    author: item.author || item.user_name || item.username || session.user?.name || session.user?.username || 'NetFlyr',
    authorAvatar: item.authorAvatar || item.user_img || item.profile_pic || item.user_image || session.user?.user_img || '',
    postedAt: item.posted_at || item.post_date || item.created_at || item.created_time || item.updated_at || item.created_date || dateValue,
    parsedDate,
    displayDate: formatFlyerListDate(parsedDate || dateValue) || 'No date',
    groupKey: parsedDate ? parsedDate.toISOString().slice(0, 10) : `undated-${index}`
  }
}

const groupedFlyers = computed(() => {
  const groups = new Map()

  flyers.value.forEach((flyer) => {
    if (!groups.has(flyer.groupKey)) {
      groups.set(flyer.groupKey, {
        key: flyer.groupKey,
        label: flyer.displayDate,
        sortValue: flyer.parsedDate ? flyer.parsedDate.getTime() : 0,
        items: []
      })
    }

    groups.get(flyer.groupKey).items.push(flyer)
  })

  return Array.from(groups.values())
    .sort((a, b) => b.sortValue - a.sortValue)
    .map((group) => ({
      ...group,
      columns: distributeByColumn(group.items, masonryColumnCount.value)
    }))
})

const flattenDateBuckets = (payload) => {
  if (!payload || typeof payload !== 'object') return []
  return Object.entries(payload).flatMap(([dateKey, items = []]) =>
    Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          created_date: item.created_date || dateKey
        }))
      : []
  )
}

const fallbackFlyers = () =>
  sampleFlyers.map((flyer, index) => normalizeFlyer(flyer, index))

const loadFlyers = async () => {
  if (!session.userId) {
    flyers.value = fallbackFlyers().sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
    return
  }

  isLoading.value = true

  try {
    const { data } = await api.listAddedFlyerLive({
      page: 1,
      user_id: session.userId,
      login_user_id: session.userId
    })

    const rows = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.list)
        ? data.list
        : flattenDateBuckets(data?.list)

    flyers.value = rows.length
      ? rows.map(normalizeFlyer).sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
      : fallbackFlyers().sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
  } catch {
    flyers.value = fallbackFlyers().sort((a, b) => (b.parsedDate?.getTime?.() || 0) - (a.parsedDate?.getTime?.() || 0))
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  updateMasonryColumnCount()
  window.addEventListener('resize', updateMasonryColumnCount)
  loadFlyers()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMasonryColumnCount)
})
</script>

<style scoped>
.created-timeline-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.created-timeline-column {
  display: grid;
  min-width: 0;
  gap: 1rem;
  align-content: start;
}

.created-timeline-card {
  min-width: 0;
}

.created-timeline-column :deep(.flyer-card-skeleton) {
  margin-bottom: 0;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 2;
}

@media (min-width: 768px) {
  .created-timeline-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .created-timeline-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
